import React, { useState, useCallback, useMemo } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  Customized,
  TooltipProps,
} from "recharts";

import { formatTime } from "../../hooks/ThermalHeatMapHooks";
import { SunPhaseSvg, MoonPhaseSvg } from "../controls/ThermalHeatMapSvgs";

// ─── Shared constants (duplicated here to avoid circular imports) ─────────────

const BREACH_COLORS = {
  max: { fill: "rgba(255,107,107,0.18)", stroke: "#ff6b6b" },
  min: { fill: "rgba(107,197,255,0.18)", stroke: "#6bc5ff" },
};

const AXIS_CONFIG = {
  tickFill: "#909090",
  tickFontSize: 10,
  stroke: "#444",
  labelFill: "rgba(144,144,144,0.7)",
  labelFontSize: 10,
};

const ZOOM_AREA_CONFIG = {
  fill: "rgba(255,255,255,0.15)",
  stroke: "rgba(255,255,255,0.3)",
  strokeWidth: 1,
};

export const PHASE_STRIP_COLORS: Record<"Eclipse" | "Sunlit", string> = {
  Sunlit: "#f0c419",
  Eclipse: "#3454a8",
};

const PHASE_STRIP_HEIGHT = 4;

// ─── X-axis phase strip (sunlit/eclipse timeline drawn just below the plot area) ──

interface PhaseAxisStripProps {
  xAxisMap?: Record<string, { scale: (value: number) => number; domain: [number, number] }>;
  offset?: { top: number; left: number; width: number; height: number };
  segments: { x1: number; x2: number; type: "Eclipse" | "Sunlit" }[];
  isFullscreen?: boolean;
}

const PHASE_STRIP_ICON_SIZE = 12;
const PHASE_STRIP_ICON_MIN_SEGMENT_WIDTH = 14;

const PhaseAxisStrip: React.FC<PhaseAxisStripProps> = ({ xAxisMap, offset, segments, isFullscreen = false }) => {
  if (!xAxisMap || !offset || segments.length === 0) return null;
  const axis = Object.values(xAxisMap)[0];
  if (!axis) return null;
  const [domainMin, domainMax] = axis.domain;
  const y = offset.top + offset.height + 0;

  return (
    <g>
      {segments.map((seg, i) => {
        const clampedX1 = Math.max(seg.x1, domainMin);
        const clampedX2 = Math.min(seg.x2, domainMax);
        if (clampedX2 <= clampedX1) return null;
        const pixelX1 = axis.scale(clampedX1);
        const pixelX2 = axis.scale(clampedX2);
        const segmentWidth = pixelX2 - pixelX1;
        const PhaseIcon = seg.type === "Sunlit" ? SunPhaseSvg : MoonPhaseSvg;
        return (
          <React.Fragment key={i}>
            <rect
              x={pixelX1}
              y={y}
              width={Math.max(0, segmentWidth)}
              height={PHASE_STRIP_HEIGHT}
              fill={PHASE_STRIP_COLORS[seg.type]}
              opacity={0.85}
            />
            {isFullscreen && segmentWidth >= PHASE_STRIP_ICON_MIN_SEGMENT_WIDTH && (
              <g
                transform={`translate(${(pixelX1 + pixelX2) / 2 - PHASE_STRIP_ICON_SIZE / 2}, ${
                  y - PHASE_STRIP_ICON_SIZE - 2
                })`}
              >
                <PhaseIcon />
              </g>
            )}
          </React.Fragment>
        );
      })}
    </g>
  );
};

export const FACE_COLORS: Record<string, string> = {
  PX:   "#36F097",
  NX:   "#5BC0D7",
  PY:   "#CCF54E",
  NY:   "#EFA514",
  PZ:   "#F43FD0",
  NZ:   "#4B8BFF",
  INT1: "#A45ED4",
  INT2: "#FC0C6D",
};

export const FACE_LABEL: Record<string, string> = {
  PX:   "+X", NX:   "−X",
  PY:   "+Y", NY:   "−Y",
  PZ:   "+Z", NZ:   "−Z",
  INT1: "Deck 1", INT2: "Deck 2",
};

// Helper to get color for a key (including _max/_min suffixes)
export function getFaceColor(key: string): string {
  const baseKey = key.replace(/_(max|min)$/, '');
  const color = FACE_COLORS[baseKey] || '#888';
  if (key.endsWith('_min')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.6)`;
  }
  return color;
}

export function getFaceLabel(key: string): string {
  const baseKey = key.replace(/_(max|min)$/, '');
  const baseLabel = FACE_LABEL[baseKey] ?? baseKey;
  if (key.endsWith('_max')) return `${baseLabel} (Max)`;
  if (key.endsWith('_min')) return `${baseLabel} (Min)`;
  return baseLabel;
}

export function getChartTitle(
  visibleFaceKeys: string[],
  totalFaceCount: number,
  selectedNode: number | null,
  isSingleFace: boolean,
  showOnlySubsystems: boolean = false
): string {
  let facesPart: string;

  if (visibleFaceKeys.length === 0) {
    facesPart = "No Faces Selected";
  } else if (visibleFaceKeys.length === totalFaceCount) {
    facesPart = "All Faces & Decks";
  } else if (visibleFaceKeys.length <= 3) {
    facesPart = visibleFaceKeys.map(getFaceLabel).join(", ");
  } else {
    facesPart = `${visibleFaceKeys.length} Faces`;
  }

  let tempMetric: string;
  if (selectedNode !== null) {
    tempMetric = "Temperature (°C)";
  } else if (isSingleFace) {
    tempMetric = showOnlySubsystems ? "Subsystem Node Temperature (°C)" : "Node Temperature (°C)";
  } else {
    tempMetric = showOnlySubsystems ? "Subsystem Temperature (°C)" : "Avg Temperature (°C)";
  }

  const nodePrefix = selectedNode !== null ? `Node ${selectedNode} — ` : "";
  return `${nodePrefix}${facesPart} — ${tempMetric} vs Time`;
}

// ─── Props ────────────────────────────────────────────────────────────────────



export interface ChartContentProps {
  chartData: Record<string, number>[];
  visibleFaceKeys: string[];
  thresholdLines: { key: string; y: number; color: string; breach?: 'max' | 'min' }[];
  breachAreas: { x1: number; x2: number; color: string; stroke: string }[];
  eclipseAreas?: { x1: number; x2: number; color: string; stroke: string }[];
  phaseStripSegments?: { x1: number; x2: number; type: "Eclipse" | "Sunlit" }[];
  renderTooltip: (props: TooltipProps<number, string>) => React.ReactNode;
  onZoomSelect?: (domain: [number, number]) => void;
  lineStyles?: Record<string, { color: string; label: string }>;
  isFullscreen?: boolean;
}

// ─── Threshold Line Label Component ──────────────────────────────────────────

interface ThresholdLabelsProps {
  yAxisMap?: Record<string, any>;
  offset?: { top: number; left: number; width: number; height: number };
  thresholdLines?: { y: number; color: string; breach?: 'max' | 'min' }[];
}

const ThresholdLabels: React.FC<ThresholdLabelsProps> = ({ yAxisMap, offset, thresholdLines = [] }) => {
  if (!yAxisMap || !offset || thresholdLines.length === 0) return null;

  const yAxis = Object.values(yAxisMap)[0] as any;
  if (!yAxis || !yAxis.scale) return null;

  const chartRightX = offset.left + offset.width - 8;

  return (
    <g>
      {thresholdLines.map((tl, i) => {
        const isMax = tl.breach === 'max';
        const pixelY = yAxis.scale(tl.y);
        const dy = -7;
        const finalY = pixelY + dy;
        const text = isMax ? 'Threshold limit max' : 'Threshold limit min';
        const labelColor = tl.color && tl.color !== '#666' ? tl.color : tl.color;

        return (
          <text
            key={`threshold-label-${i}`}
            x={chartRightX}
            y={finalY}
            fill={labelColor}
            style={{ fill: labelColor }}
            fontSize="0.6rem"
            textAnchor="end"
            dominantBaseline="auto"
            pointerEvents="none"
          >
            {text}
          </text>
        );
      })}
    </g>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const ThermalHeatMapChartContent: React.FC<ChartContentProps> = ({
  chartData,
  visibleFaceKeys,
  thresholdLines,
  breachAreas,
  eclipseAreas = [],
  phaseStripSegments = [],
  renderTooltip,
  onZoomSelect,
  lineStyles = {},
  isFullscreen = false,
}) => {
  const [refAreaLeft, setRefAreaLeft] = useState<number | "">("");
  const [refAreaRight, setRefAreaRight] = useState<number | "">("");

  const renderPhaseAxisStrip = useCallback(
    (props: any) => <PhaseAxisStrip {...props} segments={phaseStripSegments} isFullscreen={isFullscreen} />,
    [phaseStripSegments, isFullscreen]
  );

  const renderThresholdLabels = useCallback(
    (props: any) => <ThresholdLabels {...props} thresholdLines={thresholdLines} />,
    [thresholdLines]
  );

  const handleZoomStart = useCallback((e: any) => {
    if (e.activeLabel !== undefined && e.activeLabel !== null) {
      setRefAreaLeft(e.activeLabel);
      setRefAreaRight("");
    }
  }, []);

  const handleZoomMove = useCallback((e: any) => {
    if (refAreaLeft !== "" && e.activeLabel !== undefined && e.activeLabel !== null) {
      setRefAreaRight(e.activeLabel);
    }
  }, [refAreaLeft]);

  const handleZoomEnd = useCallback(() => {
    if (refAreaLeft !== "" && refAreaRight !== "" && onZoomSelect) {
      const [min, max] = refAreaLeft < refAreaRight
        ? [refAreaLeft, refAreaRight]
        : [refAreaRight, refAreaLeft];
      onZoomSelect([min, max]);
    }
    setRefAreaLeft("");
    setRefAreaRight("");
  }, [refAreaLeft, refAreaRight, onZoomSelect]);

  const handleZoomCancel = useCallback(() => {
    setRefAreaLeft("");
    setRefAreaRight("");
  }, []);

  const yDomain = useMemo(() => {
    if (thresholdLines.length === 0) return ["auto", "auto"];

    let dataMin = Infinity, dataMax = -Infinity;
    for (const pt of chartData) {
      for (const key of visibleFaceKeys) {
        const v = pt[key];
        if (typeof v === 'number') {
          dataMin = Math.min(dataMin, v);
          dataMax = Math.max(dataMax, v);
        }
      }
    }

    for (const tl of thresholdLines) {
      dataMin = Math.min(dataMin, tl.y);
      dataMax = Math.max(dataMax, tl.y);
    }

    const MARGIN_STEP = 5;
    const domainMin = isFinite(dataMin) ? Math.floor(dataMin / MARGIN_STEP) * MARGIN_STEP : -50;
    const domainMax = isFinite(dataMax) ? Math.ceil(dataMax / MARGIN_STEP) * MARGIN_STEP : 50;

    return [domainMin, domainMax];
  }, [chartData, thresholdLines, visibleFaceKeys]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={chartData}
        margin={{ top: 8, right: 20, bottom: 30, left: 8 }}
        onMouseDown={handleZoomStart}
        onMouseMove={handleZoomMove}
        onMouseUp={handleZoomEnd}
        onMouseLeave={handleZoomCancel}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.18)" />
        <XAxis
          dataKey="time"
          type="number"
          domain={["dataMin", "dataMax"]}
          tickFormatter={(v: number) => formatTime(v)}
          tick={{ fill: AXIS_CONFIG.tickFill, fontSize: AXIS_CONFIG.tickFontSize }}
          stroke={AXIS_CONFIG.stroke}
          label={{
            value: "Time (hh:mm:ss)",
            position: "insideBottom",
            offset: -12,
            fill: AXIS_CONFIG.labelFill,
            fontSize: AXIS_CONFIG.labelFontSize,
          }}
        />
        <YAxis
          domain={yDomain}
          tick={{ fill: AXIS_CONFIG.tickFill, fontSize: AXIS_CONFIG.tickFontSize }}
          stroke={AXIS_CONFIG.stroke}
          tickFormatter={(v: number) => `${v}°`}
          width={40}
          label={{
            value: "Temperature (°C)",
            angle: -90,
            position: "insideLeft",
            offset: 12,
            fill: AXIS_CONFIG.labelFill,
            fontSize: AXIS_CONFIG.labelFontSize,
            dx: -8,
            style: { textAnchor: "middle" },
          }}
        />
        <Tooltip content={renderTooltip} />

        {/* Drag-to-zoom selection area */}
        {refAreaLeft !== "" && refAreaRight !== "" && (
          <ReferenceArea
            x1={refAreaLeft}
            x2={refAreaRight}
            fill={ZOOM_AREA_CONFIG.fill}
            stroke={ZOOM_AREA_CONFIG.stroke}
            strokeWidth={ZOOM_AREA_CONFIG.strokeWidth}
            ifOverflow="hidden"
          />
        )}

        {/* Eclipse background bands */}
        {eclipseAreas.map((area, i) => (
          <ReferenceArea
            key={`eclipse-${i}`}
            x1={area.x1}
            x2={area.x2}
            fill={area.color}
            stroke={area.stroke}
            strokeOpacity={0.4}
            strokeWidth={0.75}
            ifOverflow="hidden"
          />
        ))}

        {/* Vertical breach bands */}
        {breachAreas.map((area, i) => (
          <ReferenceArea
            key={i}
            x1={area.x1}
            x2={area.x2}
            fill={area.color}
            stroke={area.stroke}
            strokeOpacity={0.75}
            strokeWidth={0.75}
            ifOverflow="hidden"
          />
        ))}

        {/* Horizontal threshold reference lines */}
        {thresholdLines.map((tl) => (
          <ReferenceLine
            key={tl.key}
            y={tl.y}
            stroke={tl.color}
            strokeDasharray="5 3"
            strokeWidth={1}
            ifOverflow="extendDomain"
          />
        ))}

        {/* Threshold line labels */}
        {thresholdLines.length > 0 && (
          <Customized component={renderThresholdLabels} />
        )}

        {/* Lines for each visible node */}
        {visibleFaceKeys.map((key) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={lineStyles[key]?.color ?? getFaceColor(key)}
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0 }}
            isAnimationActive={false}
          />
        ))}

        {/* X-axis phase strip (sunlit/eclipse timeline) */}
        {phaseStripSegments.length > 0 && (
          <Customized component={renderPhaseAxisStrip} />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};


export default React.memo(ThermalHeatMapChartContent);
